import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Sparkles,
  Loader2,
  Search,
  Car,
  Trash2,
  Crown,
  Plus,
} from "lucide-react";
import {
  SERVICES,
  SITE,
  priceForSize,
  mergeLiveServices,
  type ServiceId,
  type CarSize,
  type Service,
  type LiveServiceRow,
} from "@/lib/site";
import { useAction, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { api } from "@/lib/convex";
import { getStoredAdRef } from "@/lib/adTracking";
import { trackLead } from "@/lib/metaPixel";
import logo from "@/assets/logo.webp";
// Same files the homepage uses, so these come from cache on a warm visit.
import vaskCategoryImg from "@/assets/after-porsche-sm.webp";
import andreCategoryImg from "@/assets/services-porsche.webp";
import { sjekkEpost } from "@/lib/epostsjekk";

const BUSINESS_ID = "jh77w8p1ewdwak4qqg7k8fjben8b8zn5";

type VehicleInfo = {
  regnummer: string;
  merke: string;
  modell: string;
  aarsmodell: number | null;
  drivstoff: string | null;
  girkasse: string | null;
  farge: string | null;
  kjoretoytypeKode: string | null;
  karosseritype: string | null;
  sitteplasser: number | null;
  egenvekt: number | null;
};
type BlockedTimeRow = {
  id: string;
  block_date: string;
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
};
type SpecialHoursRow = {
  id: string;
  special_date: string;
  start_time: string | null;
  end_time: string | null;
  is_closed: boolean;
  reason: string | null;
};

function servicePriceDisplay(s: Service, size: CarSize): string {
  if (s.priceSmall == null || s.priceLarge == null) return "Etter avtale";
  return (s.fromPrice ? "fra " : "") + formatKr(priceForSize(s, size));
}

function detectCarSize(info: VehicleInfo): CarSize {
  const text = `${info.karosseritype ?? ""} ${info.kjoretoytypeKode ?? ""}`.toLowerCase();
  const storIndikatorer = [
    "stasjonsvogn",
    "suv",
    "varebil",
    "buss",
    "combi",
    "flerbruksbil",
    "lastebil",
    "pickup",
  ];
  return storIndikatorer.some((k) => text.includes(k)) ? "stor" : "liten";
}

const searchSchema = z.object({ service: z.string().optional() });

const TITLE = "Book time | Drammen Car Wash";
const DESC =
  "Book bilvask, dekkskift eller oljeskift hos Drammen Car Wash i Drammen. Finn bilen din automatisk med regnummer.";

export const Route = createFileRoute("/book")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: BookPage,
});

const STEPS = ["Reg.nr", "Tjeneste(r)", "Tillegg", "Dato og tid", "Oppsummering"];

// Fallback only. The real number comes from the server, because capacity is a
// staffing decision the shop makes in Ascendly (one person on shift means one
// car at a time), and a hardcoded 2 here would keep offering a slot the server
// then refuses — the customer picks a time and gets an error instead of seeing
// it greyed out.
const DEFAULT_MAX_BOOKINGS_PER_SLOT = 2;

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function osloOffsetMinutesAt(utcMs: number): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Oslo",
    timeZoneName: "shortOffset",
  }).formatToParts(new Date(utcMs));
  const tzPart = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT+1";
  const match = tzPart.match(/GMT([+-]\d+)/);
  return match ? parseInt(match[1], 10) * 60 : 60;
}

function osloMidnightUtcMs(d: Date): number {
  const guessUtc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0);
  return guessUtc - osloOffsetMinutesAt(guessUtc) * 60 * 1000;
}

const OSLO_WEEKDAY: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function osloDayOfWeek(dayStartUtcMs: number): number {
  const label = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Oslo",
    weekday: "short",
  }).format(new Date(dayStartUtcMs + 12 * 60 * 60 * 1000));
  return OSLO_WEEKDAY[label] ?? new Date(dayStartUtcMs).getUTCDay();
}

function fmtOsloTime(utcMs: number): string {
  return new Intl.DateTimeFormat("nb-NO", {
    timeZone: "Europe/Oslo",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(utcMs));
}

/** True if the whole date is closed, via a whole-day block or a special-hours closure. */
function isDateFullyBlocked(
  iso: string,
  blockedTimes: BlockedTimeRow[],
  specialHours: SpecialHoursRow[],
): boolean {
  if (specialHours.some((s) => s.special_date === iso && s.is_closed)) return true;
  if (blockedTimes.some((b) => b.block_date === iso && !b.start_time && !b.end_time)) return true;
  return false;
}

/** Blocked minute ranges for a specific date, from partial-day blocks. */
function blockedRangesForDate(
  iso: string,
  blockedTimes: BlockedTimeRow[],
): { start: number; end: number }[] {
  return blockedTimes
    .filter((b) => b.block_date === iso && b.start_time && b.end_time)
    .map((b) => ({ start: timeToMinutes(b.start_time!), end: timeToMinutes(b.end_time!) }));
}

const ADDON_IDS: ServiceId[] = ["felgrens", "oljeskift", "rustfjerning", "motorromrens"];
const FEATURED_SERVICE_ID: ServiceId = "gull-komplett";
const VASK_IDS: ServiceId[] = ["innvendig", "utvendig", "komplett", "premium"];

type ServiceCategoryId = "vask" | "andre";
type ServiceCategoryGroup = {
  id: ServiceCategoryId;
  title: string;
  services: Service[];
  image: string;
};

/**
 * Groups a (possibly live-merged) services list into the fixed local categories. The set of
 * services and their order always comes from `services` (i.e. the local SERVICES list, or that
 * list with live Convex fields overlaid) — this only buckets them, it never adds or reorders.
 */
function buildServiceCategories(services: readonly Service[]): ServiceCategoryGroup[] {
  return [
    {
      id: "vask",
      title: "Vask",
      services: services.filter((s) => VASK_IDS.includes(s.id)),
      image: vaskCategoryImg,
    },
    {
      id: "andre",
      title: "Polering, dekk & andre tjenester",
      services: services.filter((s) => s.id !== FEATURED_SERVICE_ID && !VASK_IDS.includes(s.id)),
      image: andreCategoryImg,
    },
  ];
}

const contactSchema = z.object({
  name: z.string().trim().min(2, "Skriv inn fullt navn").max(80),
  phone: z.string().trim().min(6, "Skriv inn et gyldig telefonnummer").max(20),
  email: z.string().trim().email("Ugyldig e-postadresse").max(120),
  note: z.string().max(500).optional(),
});

type Contact = z.infer<typeof contactSchema>;

function formatKr(n: number) {
  return n.toLocaleString("no-NO") + " kr";
}

/** Prices are shown VAT-inclusive; this derives the 25% MVA portion for the receipt breakdown. */
function vatPortion(total: number) {
  return Math.round(total - total / 1.25);
}

function toISODate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function BookPage() {
  const { service: preselect } = Route.useSearch();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [regnummer, setRegnummer] = useState("");
  const [vehicle, setVehicle] = useState<VehicleInfo | null>(null);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [vehicleError, setVehicleError] = useState<string | null>(null);
  const [carSize, setCarSize] = useState<CarSize>("liten");

  const [selected, setSelected] = useState<Set<ServiceId>>(
    () => new Set(preselect ? [preselect] : []),
  );
  const [contact, setContact] = useState<Contact>({ name: "", phone: "", email: "", note: "" });
  // Dekkhotell er ikke en egen tjeneste med tid og pris, men et valg ved
  // dekkskift. Ascendly leser «Dekkhotell» i bestillingen og ber eieren
  // registrere dekkene i dekkhotellet.
  const [dekkhotell, setDekkhotell] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState("+47");
  const [vehicleModel, setVehicleModel] = useState("");
  const [errors, setErrors] = useState<Partial<Record<keyof Contact, string>>>({});
  const [date, setDate] = useState<Date | undefined>();
  const [time, setTime] = useState<number | undefined>();
  const [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Live Convex service data (price, description, includes, duration), matched onto the local
  // SERVICES list by name. Falls back to the local static values while this query is loading.
  const liveServicesRaw = useQuery(api.bookings.listServicesForBusiness, {
    businessId: BUSINESS_ID,
  }) as LiveServiceRow[] | undefined;
  const services = useMemo(() => mergeLiveServices(SERVICES, liveServicesRaw), [liveServicesRaw]);

  const total = useMemo(
    () =>
      services
        .filter((s) => selected.has(s.id))
        .reduce((sum, s) => sum + priceForSize(s, carSize), 0),
    [services, selected, carSize],
  );

  const totalDurationMinutes = useMemo(
    () => services.filter((s) => selected.has(s.id)).reduce((sum, s) => sum + s.durationMinutes, 0),
    [services, selected],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, submitted]);

  const blockedTimesRaw = useQuery(api.availability.listBlockedTimesForBusiness, {
    businessId: BUSINESS_ID,
  }) as
    | Array<{
        _id: string;
        blockDate: string;
        startMinute?: number;
        endMinute?: number;
        reason?: string;
      }>
    | undefined;
  const specialHoursRaw = useQuery(api.availability.listSpecialHoursForBusiness, {
    businessId: BUSINESS_ID,
  }) as
    | Array<{
        _id: string;
        specialDate: string;
        startMinute?: number;
        endMinute?: number;
        isClosed: boolean;
        reason?: string;
      }>
    | undefined;

  const blockedTimes: BlockedTimeRow[] = (blockedTimesRaw ?? []).map((b) => ({
    id: b._id,
    block_date: b.blockDate,
    start_time:
      b.startMinute != null
        ? `${String(Math.floor(b.startMinute / 60)).padStart(2, "0")}:${String(b.startMinute % 60).padStart(2, "0")}`
        : null,
    end_time:
      b.endMinute != null
        ? `${String(Math.floor(b.endMinute / 60)).padStart(2, "0")}:${String(b.endMinute % 60).padStart(2, "0")}`
        : null,
    reason: b.reason ?? null,
  }));
  const specialHours: SpecialHoursRow[] = (specialHoursRaw ?? []).map((s) => ({
    id: s._id,
    special_date: s.specialDate,
    start_time:
      s.startMinute != null
        ? `${String(Math.floor(s.startMinute / 60)).padStart(2, "0")}:${String(s.startMinute % 60).padStart(2, "0")}`
        : null,
    end_time:
      s.endMinute != null
        ? `${String(Math.floor(s.endMinute / 60)).padStart(2, "0")}:${String(s.endMinute % 60).padStart(2, "0")}`
        : null,
    is_closed: s.isClosed,
    reason: s.reason ?? null,
  }));

  const businessHours = useQuery(api.bookings.listBusinessHours, { businessId: BUSINESS_ID }) as
    | Array<{ dayOfWeek: number; openMinute: number; closeMinute: number }>
    | undefined;

  const dayStart = date ? osloMidnightUtcMs(date) : undefined;
  const dayEnd = dayStart !== undefined ? dayStart + 24 * 60 * 60 * 1000 : undefined;
  const dayOfWeek = dayStart !== undefined ? osloDayOfWeek(dayStart) : undefined;

  // A special-hours override for this exact date (e.g. extended Saturday
  // hours) takes precedence over the regular weekly schedule below.
  const todaySpecialRaw = date
    ? specialHoursRaw?.find((s) => s.specialDate === toISODate(date))
    : undefined;
  const todayHours = todaySpecialRaw
    ? todaySpecialRaw.isClosed ||
      todaySpecialRaw.startMinute == null ||
      todaySpecialRaw.endMinute == null
      ? undefined
      : { openMinute: todaySpecialRaw.startMinute, closeMinute: todaySpecialRaw.endMinute }
    : businessHours?.find((h) => h.dayOfWeek === dayOfWeek);

  // Faste starttider fra Ascendly (23.10.2026: hvert kvarter, én bil per tid).
  // Null betyr ingen faste tider: da gjelder hele timer og kapasiteten under.
  const starttider = useQuery(
    api.websiteBookings.starttiderForDag,
    date ? { businessId: BUSINESS_ID, dato: toISODate(date) } : "skip",
  ) as { minutter: number[]; antall: number } | null | undefined;

  // Mens starttidene hentes vises ingen tider, ikke hele timer som forsvinner.
  const TIME_SLOTS: number[] = date && starttider === undefined
    ? []
    : starttider && dayStart !== undefined
    ? starttider.minutter.map((m) => dayStart + m * 60 * 1000)
    : todayHours
    ? (() => {
        const slots: number[] = [];
        for (let m = todayHours.openMinute; m < todayHours.closeMinute; m += 60) {
          slots.push(dayStart! + m * 60 * 1000);
        }
        return slots;
      })()
    : [];
  const CLOSING_TIME_MS =
    todayHours && dayStart !== undefined
      ? dayStart + todayHours.closeMinute * 60 * 1000
      : undefined;

  const slotCountsRaw = useQuery(
    api.websiteBookings.listSlotCountsForBusiness,
    dayStart !== undefined && dayEnd !== undefined
      ? { businessId: BUSINESS_ID, dayStart, dayEnd }
      : "skip",
  ) as Record<number, number> | undefined;
  const bookedCounts: Record<number, number> = slotCountsRaw ?? {};

  const capacityRaw = useQuery(api.websiteBookings.getSlotCapacity, {
    businessId: BUSINESS_ID,
  }) as { capacity: number; until?: number } | undefined;
  const maxPerSlot = starttider ? starttider.antall : (capacityRaw?.capacity ?? DEFAULT_MAX_BOOKINGS_PER_SLOT);

  useEffect(() => {
    // Also runs when capacity drops: someone who had already picked 12:00 gets
    // it cleared rather than carrying a now-invalid time into the summary step.
    if (time && (bookedCounts[time] ?? 0) >= maxPerSlot) {
      setTime(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookedCounts, maxPerSlot]);

  const lookupVehicleAction = useAction(api.vehicleLookup.lookupByRegistration);
  const createWebsiteBooking = useMutation(api.websiteBookings.createWebsiteBooking);

  const searchVehicle = async () => {
    const plate = regnummer.trim();
    if (!plate) return;
    setVehicleLoading(true);
    setVehicleError(null);
    try {
      const info = (await lookupVehicleAction({ regnummer: plate })) as VehicleInfo;
      setVehicle(info);
      setCarSize(detectCarSize(info));
      setVehicleModel([info.merke, info.modell].filter(Boolean).join(" "));
    } catch (err) {
      setVehicle(null);
      setVehicleError(
        err instanceof ConvexError
          ? (err.data as string)
          : "Fant ingen bil med dette regnummeret. Sjekk at det er riktig skrevet.",
      );
      console.error(err);
    } finally {
      setVehicleLoading(false);
    }
  };

  const clearVehicle = () => {
    setVehicle(null);
    setRegnummer("");
    setVehicleError(null);
    setVehicleModel("");
  };

  const canNext =
    (step === 1 && !!vehicle) ||
    (step === 2 && selected.size > 0) ||
    step === 3 ||
    (step === 4 &&
      !!date &&
      time !== undefined &&
      time >= Date.now() &&
      CLOSING_TIME_MS !== undefined &&
      time + totalDurationMinutes * 60 * 1000 <= CLOSING_TIME_MS &&
      (bookedCounts[time] ?? 0) < maxPerSlot &&
      !isDateFullyBlocked(toISODate(date), blockedTimes, specialHours) &&
      !blockedRangesForDate(toISODate(date), blockedTimes).some(
        (r) =>
          timeToMinutes(fmtOsloTime(time)) >= r.start && timeToMinutes(fmtOsloTime(time)) < r.end,
      )) ||
    (step === 5 && contactSchema.safeParse(contact).success && confirmed && !submitting);

  // Avkrysningen gjelder dekkskift. Har kunden allerede valgt tjenesten
  // «Dekkhotell», er den overfloedig.
  const valgteNavn = services.filter((s) => selected.has(s.id)).map((s) => s.name);
  const harDekkskift =
    valgteNavn.some((n) => /dekkskift|hjulskift/i.test(n)) && !valgteNavn.some((n) => /dekkhotell/i.test(n));
  const medDekkhotell = dekkhotell && harDekkskift;

  const toggle = (id: ServiceId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const goNext = async () => {
    if (step === 5) {
      const res = contactSchema.safeParse(contact);
      if (!res.success) {
        const errs: Partial<Record<keyof Contact, string>> = {};
        for (const issue of res.error.issues) {
          const key = issue.path[0] as keyof Contact;
          if (!errs[key]) errs[key] = issue.message;
        }
        setErrors(errs);
        return;
      }
      setErrors({});
      if (!date || !time) return;
      setSubmitting(true);
      setSubmitError(null);
      const items = services.filter((s) => selected.has(s.id));
      const fullPhone = `${phoneCountryCode.trim()}${contact.phone.trim()}`;
      const carLabel = [
        vehicleModel.trim() || `${vehicle?.merke ?? ""} ${vehicle?.modell ?? ""}`.trim(),
        vehicle?.aarsmodell,
      ]
        .filter(Boolean)
        .join(" ");
      try {
        const endTime = time + totalDurationMinutes * 60 * 1000;
        await createWebsiteBooking({
          businessId: BUSINESS_ID,
          customerName: contact.name,
          customerEmail: contact.email,
          customerPhone: fullPhone,
          serviceLabel: [...items.map((s) => s.name), ...(medDekkhotell ? ["Dekkhotell"] : [])].join(" + "),
          totalPrice: total,
          vehicleLabel: carLabel || undefined,
          licensePlate: vehicle?.regnummer,
          note:
            [medDekkhotell ? "Ønsker dekkhotell (oppbevaring av dekkene, pris avtales)" : "", contact.note]
              .filter(Boolean)
              .join("\n") || undefined,
          startTime: time,
          endTime,
          adRef: getStoredAdRef(),
        });
        // Only after the booking is actually persisted, so Meta never counts a
        // lead for a submission that failed.
        trackLead({
          value: total,
          contentName: items.map((s) => s.name).join(" + "),
          eventId: `booking-${time}-${fullPhone}`,
        });
        setSubmitted(true);
      } catch (err) {
        const message = err instanceof ConvexError ? String(err.data) : "";
        if (message.includes("fullbooket")) {
          setSubmitError(
            "Beklager, dette tidspunktet ble akkurat fullbooket av en annen kunde. Velg et nytt tidspunkt.",
          );
          setStep(4);
          setTime(undefined);
        } else {
          setSubmitError("Noe gikk galt. Prøv igjen, eller kontakt oss direkte.");
        }
        console.error(err);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setStep((s) => Math.min(5, s + 1));
  };

  if (submitted) return <SuccessScreen onHome={() => navigate({ to: "/" })} />;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Minimal top bar */}
      <header className="border-b border-border">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 flex items-center justify-between py-2">
          <Link to="/" aria-label={SITE.name}>
            <img
              src={logo}
              alt={`${SITE.name} logo`}
              className="h-28 w-28 md:h-40 md:w-40 object-contain"
            />
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
            Avbryt
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-10 sm:py-14 pb-40 lg:pb-24 grid gap-8 lg:grid-cols-[1fr_340px] items-start">
        <div>
          <StepIndicator step={step} />

          <div className="mt-10">
            {step === 1 && (
              <StepReg
                regnummer={regnummer}
                setRegnummer={setRegnummer}
                vehicle={vehicle}
                loading={vehicleLoading}
                error={vehicleError}
                onSearch={searchVehicle}
                onClear={clearVehicle}
                carSize={carSize}
                setCarSize={setCarSize}
              />
            )}
            {step === 2 && (
              <StepServices
                selected={selected}
                toggle={toggle}
                total={total}
                carSize={carSize}
                services={services}
              />
            )}
            {step === 2 && harDekkskift && (
              <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-2xl border border-border bg-card p-5 shadow-soft">
                <input
                  type="checkbox"
                  checked={dekkhotell}
                  onChange={(e) => setDekkhotell(e.target.checked)}
                  className="mt-1 h-5 w-5 shrink-0 accent-[hsl(var(--primary))]"
                />
                <span>
                  <span className="block font-medium">Lagre dekkene på dekkhotell</span>
                  <span className="mt-1 block text-sm text-muted-foreground">
                    Vi tar vare på dekkene du ikke bruker til neste sesong. Pris avtales ved levering.
                  </span>
                </span>
              </label>
            )}
            {step === 3 && (
              <StepAddons
                selected={selected}
                toggle={toggle}
                carSize={carSize}
                services={services}
              />
            )}
            {step === 4 && (
              <StepDateTime
                date={date}
                setDate={setDate}
                time={time}
                setTime={setTime}
                durationMinutes={totalDurationMinutes}
                bookedCounts={bookedCounts}
                maxPerSlot={maxPerSlot}
                blockedTimes={blockedTimes}
                specialHours={specialHours}
                businessHours={businessHours ?? []}
                timeSlots={TIME_SLOTS}
                closingTimeMs={CLOSING_TIME_MS}
              />
            )}
            {step === 5 && (
              <StepConfirm
                vehicleModel={vehicleModel}
                setVehicleModel={setVehicleModel}
                contact={contact}
                setContact={setContact}
                phoneCountryCode={phoneCountryCode}
                setPhoneCountryCode={setPhoneCountryCode}
                errors={errors}
              />
            )}
            {submitError && (
              <div className="mt-6 rounded-xl border border-destructive/40 bg-destructive/10 px-5 py-4 text-sm text-destructive">
                {submitError}
              </div>
            )}
            {step === 5 && (
              <div className="lg:hidden mt-6 rounded-xl border border-border bg-card p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-[oklch(0.62_0.19_258)]"
                  />
                  <span className="text-sm text-foreground/85">
                    Jeg bekrefter at opplysningene er korrekte.
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Desktop nav buttons */}
          <div className="mt-12 hidden md:flex items-center justify-between">
            {step > 1 ? (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Tilbake
              </button>
            ) : (
              <span />
            )}
            <button
              onClick={goNext}
              disabled={!canNext}
              className={`inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3 text-sm font-medium text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed shadow-luxe ${step === 5 ? "lg:hidden" : ""}`}
            >
              {submitting ? (
                <>
                  Sender <Loader2 className="h-4 w-4 animate-spin" />
                </>
              ) : (
                <>
                  {step === 5 ? "Send bestilling" : "Neste"} <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Order summary sidebar */}
        <OrderSummary
          step={step}
          regnummer={vehicle?.regnummer ?? regnummer}
          vehicle={vehicle}
          carSize={carSize}
          services={services}
          selected={selected}
          date={date}
          time={time}
          total={total}
          confirmed={confirmed}
          setConfirmed={setConfirmed}
          onSubmit={goNext}
          canSubmit={canNext}
          submitting={submitting}
        />
      </div>

      {/* Bestillingssiden har ingen bunntekst, saa krediteringen staar her.
          pb gir plass til den faste mobilbaren under, slik at logoen ikke
          havner bak den. Hvit logo fordi siden staar paa moerk bakgrunn. */}
      <div className="mx-auto max-w-6xl px-4 pt-10 pb-[calc(96px+env(safe-area-inset-bottom,0px))] md:pb-10 flex items-center justify-center text-xs text-muted-foreground">
        Laget av{" "}
        <a
          href="https://ascendly.no"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block align-middle transition hover:opacity-80"
        >
          <img
            src="/laget-av-ascendly.png"
            alt="Ascendly"
            loading="lazy"
            decoding="async"
            className="ml-1 inline-block h-8 w-auto align-middle"
          />
        </a>
      </div>

      {/* Mobile sticky bar */}
      <div className="fixed bottom-0 inset-x-0 md:hidden z-40 border-t border-border bg-background/95 backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-3">
          <div>
            {step > 1 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground"
              >
                <ArrowLeft className="h-4 w-4" /> Tilbake
              </button>
            )}
            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Totalt</div>
            <div className="font-serif text-lg leading-none">{formatKr(total)}</div>
          </div>
          <button
            onClick={goNext}
            disabled={!canNext}
            className="inline-flex items-center gap-2 rounded-full bg-primary-strong px-5 py-3 text-sm font-medium text-primary-foreground disabled:opacity-40"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {step === 5 ? "Send" : "Neste"} <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ step }: { step: number }) {
  return (
    <>
      <div className="hidden sm:flex items-center gap-2">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const active = n === step;
          const done = n < step;
          return (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div
                className={`flex items-center gap-2 ${active || done ? "text-foreground" : "text-muted-foreground"}`}
              >
                <div
                  className={`h-8 w-8 rounded-full flex items-center justify-center text-xs border transition-colors shrink-0 ${
                    active
                      ? "bg-primary-strong text-primary-foreground border-primary"
                      : done
                        ? "bg-primary/15 border-primary text-primary"
                        : "border-border"
                  }`}
                >
                  {done ? <Check className="h-4 w-4" /> : n}
                </div>
                <span className="text-xs hidden xl:inline">{label}</span>
              </div>
              {n < STEPS.length && (
                <div className={`h-px flex-1 ${done ? "bg-primary" : "bg-border"}`} />
              )}
            </div>
          );
        })}
      </div>
      <div className="sm:hidden text-sm text-muted-foreground">
        Steg {step} av {STEPS.length} · <span className="text-primary">{STEPS[step - 1]}</span>
      </div>
    </>
  );
}

function StepReg({
  regnummer,
  setRegnummer,
  vehicle,
  loading,
  error,
  onSearch,
  onClear,
  carSize,
  setCarSize,
}: {
  regnummer: string;
  setRegnummer: (v: string) => void;
  vehicle: VehicleInfo | null;
  loading: boolean;
  error: string | null;
  onSearch: () => void;
  onClear: () => void;
  carSize: CarSize;
  setCarSize: (s: CarSize) => void;
}) {
  return (
    <div>
      <h1 className="font-serif text-3xl sm:text-4xl">Skriv inn registreringsnummer</h1>
      <p className="mt-2 text-sm text-muted-foreground">Vi finner bilen din automatisk.</p>

      <div className="mt-8 flex gap-3">
        <div className="flex-1 flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3.5 focus-within:border-primary transition-colors">
          <Car className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            value={regnummer}
            onChange={(e) => setRegnummer(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && onSearch()}
            placeholder="F.eks. AB12345"
            className="flex-1 bg-transparent text-sm outline-none tracking-widest placeholder:tracking-normal placeholder:text-muted-foreground"
          />
        </div>
        <button
          onClick={onSearch}
          disabled={loading || !regnummer.trim()}
          className="inline-flex items-center gap-2 rounded-xl bg-primary-strong px-6 text-sm font-medium text-primary-foreground shadow-luxe disabled:opacity-40"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Søk
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-destructive/40 bg-destructive/10 px-5 py-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {vehicle && (
        <Reveal>
          <div className="mt-6 rounded-xl border border-primary/40 bg-primary/5 p-5 flex items-start justify-between gap-4">
            <div>
              <div className="text-xs eyebrow text-primary">Din bil</div>
              <div className="mt-2 font-serif text-xl">
                {vehicle.merke} {vehicle.modell} {vehicle.aarsmodell || ""}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                {[vehicle.drivstoff, vehicle.farge].filter(Boolean).join(" · ")}
              </div>
            </div>
            <button
              onClick={onClear}
              className="text-muted-foreground hover:text-destructive transition-colors"
              aria-label="Fjern"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-6">
            <div className="text-xs eyebrow mb-3">Størrelse på bil</div>
            <div className="grid grid-cols-2 gap-2 rounded-full border border-border p-1">
              {(["liten", "stor"] as const).map((sz) => (
                <button
                  key={sz}
                  type="button"
                  onClick={() => setCarSize(sz)}
                  className={`rounded-full px-4 py-2.5 text-sm font-medium text-center transition-colors ${
                    carSize === sz
                      ? "bg-primary-strong text-primary-foreground"
                      : "text-foreground/85 hover:text-primary"
                  }`}
                >
                  {sz === "liten" ? "Liten bil" : "Stor bil"}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Automatisk gjenkjent basert på bilmodell. Du kan endre det selv om det ikke stemmer.
            </p>
          </div>
        </Reveal>
      )}
    </div>
  );
}

function Reveal({ children }: { children: React.ReactNode }) {
  return <div className="reveal is-visible">{children}</div>;
}

function StepServices({
  selected,
  toggle,
  total,
  carSize,
  services,
}: {
  selected: Set<ServiceId>;
  toggle: (id: ServiceId) => void;
  total: number;
  carSize: CarSize;
  services: Service[];
}) {
  const featuredService = services.find((s) => s.id === FEATURED_SERVICE_ID);
  const featuredOn = featuredService ? selected.has(featuredService.id) : false;
  const categories = useMemo(() => buildServiceCategories(services), [services]);
  const [openCategory, setOpenCategory] = useState<ServiceCategoryId | null>(null);
  const activeCategory = categories.find((c) => c.id === openCategory);
  return (
    <div>
      <h1 className="font-serif text-3xl sm:text-4xl">Velg tjeneste(r)</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Du kan velge flere tjenester. Prisen oppdateres nedenfor.
      </p>

      {featuredService && (
        <button
          type="button"
          onClick={() => toggle(featuredService.id)}
          className={`mt-8 w-full text-left rounded-2xl border-2 p-6 sm:p-8 transition-all relative overflow-hidden ${
            featuredOn
              ? "border-amber-400 bg-amber-400/10 shadow-luxe"
              : "border-amber-400/50 bg-gradient-to-br from-amber-400/10 via-card to-card hover:border-amber-400"
          }`}
        >
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-amber-950">
            <Crown className="h-3.5 w-3.5" /> Vår beste pakke
          </div>
          <div className="mt-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-serif text-2xl sm:text-3xl">{featuredService.name}</div>
              <div className="text-sm text-amber-400 mt-1.5 font-medium">
                {servicePriceDisplay(featuredService, carSize)}
              </div>
            </div>
            <div
              className={`h-7 w-7 shrink-0 rounded-full border flex items-center justify-center transition-colors ${
                featuredOn ? "bg-amber-400 border-amber-400 text-amber-950" : "border-border"
              }`}
            >
              {featuredOn && <Check className="h-4 w-4" />}
            </div>
          </div>
          {featuredService.includes && (
            <ul className="mt-5 grid gap-x-6 gap-y-2 sm:grid-cols-2">
              {featuredService.includes.map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-foreground/85">
                  <Check className="h-3.5 w-3.5 shrink-0 text-amber-400" />
                  {item}
                </li>
              ))}
            </ul>
          )}
        </button>
      )}

      {!activeCategory ? (
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {categories.map((cat) => {
            const chosenCount = cat.services.filter((s) => selected.has(s.id)).length;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => setOpenCategory(cat.id)}
                className="group relative h-48 overflow-hidden rounded-2xl text-left"
              >
                <img
                  src={cat.image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/5" />
                <div className="relative flex h-full flex-col justify-end p-5">
                  <div className="font-serif text-2xl text-white">{cat.title}</div>
                  <div className="mt-1 text-sm text-white/70">
                    {cat.services.length} tjenester
                    {chosenCount > 0 ? ` · ${chosenCount} valgt` : ""}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mt-8">
          <button
            type="button"
            onClick={() => setOpenCategory(null)}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Tilbake til kategorier
          </button>
          <ServiceCategory
            title={activeCategory.title}
            services={activeCategory.services}
            selected={selected}
            toggle={toggle}
            carSize={carSize}
          />
        </div>
      )}

      <div className="mt-8 rounded-xl border border-border bg-card px-5 py-4 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Totalt (foreløpig)</div>
        <div className="font-serif text-2xl">{formatKr(total)}</div>
      </div>
    </div>
  );
}

function ServiceCategory({
  title,
  services,
  selected,
  toggle,
  carSize,
}: {
  title: string;
  services: readonly Service[];
  selected: Set<ServiceId>;
  toggle: (id: ServiceId) => void;
  carSize: CarSize;
}) {
  return (
    <div className="mt-4">
      <h2 className="text-xs eyebrow text-muted-foreground">{title}</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {services.map((s) => {
          const on = selected.has(s.id);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => toggle(s.id)}
              className={`text-left rounded-xl border p-4 sm:p-5 transition-all ${
                on
                  ? "border-primary bg-primary/5 shadow-luxe"
                  : "border-border bg-card hover:border-primary/60"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-serif text-lg">{s.name}</div>
                    {s.featured && (
                      <span className="rounded-full bg-primary/15 text-primary text-[11px] px-2.5 py-0.5 tracking-wide">
                        Mest populær
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-primary mt-1">{servicePriceDisplay(s, carSize)}</div>
                </div>
                <div
                  className={`h-6 w-6 shrink-0 rounded-full border flex items-center justify-center transition-colors ${
                    on ? "bg-primary-strong border-primary text-primary-foreground" : "border-border"
                  }`}
                >
                  {on && <Check className="h-3.5 w-3.5" />}
                </div>
              </div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepAddons({
  selected,
  toggle,
  carSize,
  services,
}: {
  selected: Set<ServiceId>;
  toggle: (id: ServiceId) => void;
  carSize: CarSize;
  services: Service[];
}) {
  const addonServices = useMemo(() => services.filter((s) => ADDON_IDS.includes(s.id)), [services]);
  return (
    <div>
      <h1 className="font-serif text-3xl sm:text-4xl">Vil du legge til noe?</h1>
      <p className="mt-2 text-sm text-muted-foreground">Valgfrie tillegg til bestillingen din.</p>

      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {addonServices.map((s) => {
          const on = selected.has(s.id);
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => toggle(s.id)}
              className={`text-left rounded-xl border p-5 transition-all ${
                on
                  ? "border-primary bg-primary/5 shadow-luxe"
                  : "border-border bg-card hover:border-primary/60"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-serif text-lg">{s.name}</div>
                  <div className="text-xs text-primary mt-1">{servicePriceDisplay(s, carSize)}</div>
                </div>
                <div
                  className={`h-6 w-6 shrink-0 rounded-full border flex items-center justify-center transition-colors ${
                    on ? "bg-primary-strong border-primary text-primary-foreground" : "border-border"
                  }`}
                >
                  {on ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </div>
              </div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const inp =
  "w-full rounded-lg border border-border bg-card px-4 py-3 text-sm outline-none focus:border-primary transition-colors";

function Field({
  label,
  children,
  error,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs eyebrow mb-2">{label}</div>
      {children}
      {error && <div className="mt-1.5 text-xs text-destructive">{error}</div>}
    </label>
  );
}

function StepDateTime({
  date,
  setDate,
  time,
  setTime,
  durationMinutes,
  bookedCounts,
  maxPerSlot,
  blockedTimes,
  specialHours,
  businessHours,
  timeSlots,
  closingTimeMs,
}: {
  date: Date | undefined;
  setDate: (d: Date | undefined) => void;
  time: number | undefined;
  setTime: (t: number) => void;
  durationMinutes: number;
  bookedCounts: Record<number, number>;
  /** How many customers may hold the same slot. Comes from the server so the
   *  greying-out here always matches what the booking mutation will accept. */
  maxPerSlot: number;
  blockedTimes: BlockedTimeRow[];
  specialHours: SpecialHoursRow[];
  businessHours: Array<{ dayOfWeek: number; openMinute: number; closeMinute: number }>;
  timeSlots: number[];
  closingTimeMs: number | undefined;
}) {
  const durationLabel =
    durationMinutes >= 60
      ? `${(durationMinutes / 60).toLocaleString("no-NO", { maximumFractionDigits: 1 })} t`
      : `${durationMinutes} min`;
  const selectedIso = date ? toISODate(date) : null;
  const blockedRanges = selectedIso ? blockedRangesForDate(selectedIso, blockedTimes) : [];
  return (
    <div>
      <h1 className="font-serif text-3xl sm:text-4xl">Velg dato og tid</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Ledige tidspunkter oppdateres kontinuerlig. Estimert varighet: {durationLabel}.
      </p>
      <div className="mt-8 grid gap-8 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
          <DayPicker
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={[
              { before: new Date() },
              (d: Date) => !businessHours?.some((h) => h.dayOfWeek === d.getDay()),
              (d: Date) => isDateFullyBlocked(toISODate(d), blockedTimes, specialHours),
            ]}
            weekStartsOn={1}
            classNames={{
              day_selected: "bg-primary-strong text-primary-foreground rounded-md",
              day_today: "text-primary",
            }}
            styles={{
              caption: { color: "var(--foreground)" },
              head_cell: { color: "var(--muted-foreground)" },
            }}
          />
        </div>
        <div>
          <div className="text-xs eyebrow mb-3">Velg tidspunkt</div>
          {!date ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-sm text-muted-foreground text-center">
              Velg først en dato
            </div>
          ) : (
            <LedigeTider
              timeSlots={timeSlots}
              time={time}
              setTime={setTime}
              erLedig={(slot) => {
                const fitsBeforeClosing =
                  closingTimeMs !== undefined && slot + durationMinutes * 60 * 1000 <= closingTimeMs;
                const isFull = (bookedCounts[slot] ?? 0) >= maxPerSlot;
                const isBlocked = blockedRanges.some((r) => {
                  const slotMinutes = timeToMinutes(fmtOsloTime(slot));
                  return slotMinutes >= r.start && slotMinutes < r.end;
                });
                return slot >= Date.now() && fitsBeforeClosing && !isFull && !isBlocked;
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Bare ledige tider, én per halvtime (Rafael 24.10.2026: «ikke vis det som
 * er fullt», «30 min incrementals»). Starttidene er hvert kvarter, men kunden
 * ser 10:00, 10:30, 11:00 … Bare naar hel eller halv time er tatt, vises
 * kvarteret etter (10:15 eller 10:45) i stedet. Neste halvtime er tilbake paa
 * hel eller halv time. Fulle, passerte og stengte tider vises ikke.
 */
function LedigeTider({
  timeSlots,
  time,
  setTime,
  erLedig,
}: {
  timeSlots: number[];
  time: number | undefined;
  setTime: (t: number) => void;
  erLedig: (slot: number) => boolean;
}) {
  const HALVTIME = 30 * 60 * 1000;
  // Foerste ledige tid i hver halvtime (hel/halv time foerst, saa kvarteret).
  const perHalvtime = new Map<number, number>();
  for (const slot of [...timeSlots].sort((a, b) => a - b)) {
    if (!erLedig(slot)) continue;
    const vindu = Math.floor(slot / HALVTIME) * HALVTIME;
    if (!perHalvtime.has(vindu)) perHalvtime.set(vindu, slot);
  }
  const synlige = [...perHalvtime.values()].sort((a, b) => a - b);
  // En tid som allerede er valgt, blir staaende synlig.
  if (time !== undefined && !synlige.includes(time) && erLedig(time)) {
    synlige.push(time);
    synlige.sort((a, b) => a - b);
  }
  if (synlige.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-sm text-muted-foreground text-center">
        Ingen ledige tider denne dagen. Velg en annen dato.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {synlige.map((slot) => {
        const active = time === slot;
        return (
          <button
            key={slot}
            onClick={() => setTime(slot)}
            className={`rounded-lg border px-3 py-3 text-sm transition-colors ${
              active ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/60"
            }`}
          >
            {fmtOsloTime(slot)}
          </button>
        );
      })}
    </div>
  );
}

function StepConfirm({
  vehicleModel,
  setVehicleModel,
  contact,
  setContact,
  phoneCountryCode,
  setPhoneCountryCode,
  errors,
}: {
  vehicleModel: string;
  setVehicleModel: (v: string) => void;
  contact: Contact;
  setContact: (c: Contact) => void;
  phoneCountryCode: string;
  setPhoneCountryCode: (v: string) => void;
  errors: Partial<Record<keyof Contact, string>>;
}) {
  const upd = <K extends keyof Contact>(k: K, v: Contact[K]) => setContact({ ...contact, [k]: v });
  const epostAdvarsel = contact.email.includes("@") ? sjekkEpost(contact.email) : null;
  return (
    <div>
      <h1 className="font-serif text-3xl sm:text-4xl">Oppsummering</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Fyll inn kontaktinfo og sjekk at alt stemmer før du sender bestillingen.
      </p>

      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        <Field label="Navn" error={errors.name}>
          <input
            value={contact.name}
            onChange={(e) => upd("name", e.target.value)}
            className={inp}
          />
        </Field>
        <Field label="Telefon" error={errors.phone}>
          <div className="flex gap-2">
            <input
              value={phoneCountryCode}
              onChange={(e) => setPhoneCountryCode(e.target.value)}
              className={inp.replace("w-full", "w-[4.5rem]") + " shrink-0 text-center"}
              inputMode="tel"
              aria-label="Landkode"
            />
            <input
              value={contact.phone}
              onChange={(e) => upd("phone", e.target.value)}
              className={inp.replace("w-full", "w-full min-w-0 flex-1")}
              inputMode="tel"
            />
          </div>
        </Field>
        <Field label="E-post" error={errors.email} className="sm:col-span-2">
          <input
            value={contact.email}
            onChange={(e) => upd("email", e.target.value)}
            className={inp}
            type="email"
          />
          {/* Formatet kan vaere helt riktig og adressen likevel feil. En kunde
              skrev "online.nog", bekreftelsen studset, og ingen fikk vite det.
              Dette blokkerer ingenting, det spoer bare. */}
          {epostAdvarsel && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-amber-300">
              <span>{epostAdvarsel.melding}</span>
              {epostAdvarsel.forslag && (
                <button
                  type="button"
                  onClick={() => upd("email", epostAdvarsel.forslag!)}
                  className="underline underline-offset-2 hover:text-amber-200"
                >
                  Bruk denne
                </button>
              )}
            </div>
          )}
        </Field>
        <Field label="Bilmerke og modell" className="sm:col-span-2">
          <input
            value={vehicleModel}
            onChange={(e) => setVehicleModel(e.target.value)}
            className={inp}
            placeholder="F.eks. Toyota Corolla"
          />
        </Field>
        <Field label="Merknad (valgfritt)" className="sm:col-span-2">
          <textarea
            value={contact.note}
            onChange={(e) => upd("note", e.target.value)}
            rows={3}
            className={inp + " resize-none"}
          />
        </Field>
      </div>
    </div>
  );
}

function OrderSummary({
  step,
  regnummer,
  vehicle,
  carSize,
  services,
  selected,
  date,
  time,
  total,
  confirmed,
  setConfirmed,
  onSubmit,
  canSubmit,
  submitting,
}: {
  step: number;
  regnummer: string;
  vehicle: VehicleInfo | null;
  carSize: CarSize;
  services: Service[];
  selected: Set<ServiceId>;
  date: Date | undefined;
  time?: number;
  total: number;
  confirmed: boolean;
  setConfirmed: (b: boolean) => void;
  onSubmit: () => void;
  canSubmit: boolean;
  submitting: boolean;
}) {
  const items = services.filter((s) => selected.has(s.id));
  const dateStr = date ? date.toLocaleDateString("no-NO", { day: "numeric", month: "short" }) : "–";
  return (
    <div className="hidden lg:block sticky top-24 rounded-2xl border border-border bg-card p-6">
      <div className="eyebrow">Din bestilling</div>
      <dl className="mt-5 space-y-4 text-sm">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-muted-foreground">Reg.nr</dt>
          <dd className="font-medium">{regnummer || "–"}</dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-muted-foreground">Bil</dt>
          <dd className="font-medium text-right">
            {vehicle ? `${vehicle.merke} ${vehicle.modell}` : "–"}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-muted-foreground">Størrelse</dt>
          <dd className="font-medium">
            {vehicle ? (carSize === "liten" ? "Liten" : "Stor") : "–"}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Tjenester</dt>
          {items.length === 0 ? (
            <dd className="mt-1 text-muted-foreground">Ingen valgt enda.</dd>
          ) : (
            <dd className="mt-2 space-y-1.5">
              {items.map((s) => (
                <div key={s.id} className="flex justify-between gap-3">
                  <span>{s.name}</span>
                  <span className="text-muted-foreground">{servicePriceDisplay(s, carSize)}</span>
                </div>
              ))}
            </dd>
          )}
        </div>
        <div className="flex items-center justify-between gap-3">
          <dt className="text-muted-foreground">Dato</dt>
          <dd className="font-medium">{dateStr}</dd>
        </div>
        {time !== undefined && (
          <div className="flex items-center justify-between gap-3">
            <dt className="text-muted-foreground">Tid</dt>
            <dd className="font-medium">kl. {fmtOsloTime(time)}</dd>
          </div>
        )}
      </dl>
      <div className="mt-6 pt-5 border-t border-border flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Totalt</span>
        <span className="font-serif text-2xl text-primary">{formatKr(total)}</span>
      </div>
      {total > 0 && (
        <div className="mt-1 text-right text-xs text-muted-foreground">
          hvorav mva (25%): {formatKr(vatPortion(total))}
        </div>
      )}

      {step === 5 && (
        <div className="mt-6 pt-5 border-t border-border">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1 h-4 w-4 accent-[oklch(0.62_0.19_258)]"
            />
            <span className="text-sm text-foreground/85">
              Jeg bekrefter at opplysningene er korrekte.
            </span>
          </label>
          {/* GDPR artikkel 13: informasjonen skal gis der opplysningene faktisk
              samles inn, ikke bare et sted på nettsiden. */}
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
            Vi bruker opplysningene til å bekrefte og minne deg på timen.{" "}
            <a
              href="/personvern"
              className="underline underline-offset-2 hover:text-primary"
            >
              Slik behandler vi personopplysninger
            </a>
            .
          </p>
          <button
            onClick={onSubmit}
            disabled={!canSubmit}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary-strong px-7 py-3 text-sm font-medium text-primary-foreground disabled:opacity-40 disabled:cursor-not-allowed shadow-luxe"
          >
            {submitting ? (
              <>
                Sender <Loader2 className="h-4 w-4 animate-spin" />
              </>
            ) : (
              <>
                Bekreft bestilling <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function SuccessScreen({ onHome }: { onHome: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <div className="mx-auto h-20 w-20 rounded-full bg-primary/15 flex items-center justify-center glow">
          <svg viewBox="0 0 60 60" className="h-10 w-10">
            <path
              d="M15 31 L26 42 L46 20"
              fill="none"
              stroke="oklch(0.62 0.19 258)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                strokeDasharray: 60,
                strokeDashoffset: 60,
                animation: "check-draw 700ms ease-out forwards",
              }}
            />
          </svg>
        </div>
        <h1 className="mt-8 font-serif text-4xl">Takk!</h1>
        <p className="mt-4 text-muted-foreground">
          Din bestilling er mottatt. Vi tar kontakt for å bekrefte.
        </p>
        <button
          onClick={onHome}
          className="mt-10 inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
        >
          <Sparkles className="h-4 w-4" /> Tilbake til forsiden
        </button>

        {/* Kvitteringsskjermen har heller ingen bunntekst. */}
        <div className="mt-14 flex items-center justify-center text-xs text-muted-foreground">
          Laget av{" "}
          <a
            href="https://ascendly.no"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block align-middle transition hover:opacity-80"
          >
            <img
              src="/laget-av-ascendly.png"
              alt="Ascendly"
              loading="lazy"
              decoding="async"
              className="ml-1 inline-block h-8 w-auto align-middle"
            />
          </a>
        </div>
      </div>
    </div>
  );
}
